<template>
    <div class="constructionstage">
        <div class="con_stage__main" :class="{'con_stage__animation': animationRunning}"
             ref="mainstage"
             @animationend="stopAnimation"
             @mousedown.left="handleMouseDown"
             @mouseup="handleMouseUp"
             @mousemove="handleMouseMove"
             @mouseleave="handleMouseLeave"
             @touchstart="handleTouchStart"
             @touchend="handleTouchEnd"
             @touchmove="handleTouchMove"
             @touchcancel="handleTouchEnd"
             @click="debugMousePosition"
        >
            <global-responsive-image v-if="configuration.image_bg" class="con_stage__img con_stage__img--bg" :responsiveImage="configuration.image_bg" />
            <global-responsive-image v-if="configuration.image_fg" class="con_stage__img con_stage__img--fg" :responsiveImage="configuration.image_fg" :style="sliderImageStyle" />
            <div v-if="showSlider" class="con_stage__handle" :style="sliderHandleStyle">
                <div class="con_stage__handle-left" :class="{'con_stage__handle-left--visible': showLeftHandle}" @click="handleLeftHandlerClick"></div>
                <div class="con_stage__handle-right" :class="{'con_stage__handle-right--visible': showRightHandle}" @click="handleRightHandlerClick"></div>
            </div>
            <template v-for="type in configuration.types">
                <div class="con_stage__marker"
                     :class="{
                        'con_stage__marker--light': type.theme_box === 'light',
                        'con_stage__marker--dark': type.theme_box === 'dark',
                        'con_stage__marker--active': selectedType === type || (hoverType === type && (!isMobile || animationRunning)),
                     }"
                     :style="getStyleForMarker(type.position)"
                     @click="toggleSelectedType(type, $event)"
                     @mouseenter="setHoverType(type)"
                     @mouseleave="setHoverType(null)"
                >
                    <div class="con_stage__marker-content" :class="{
                        'con_stage__marker-content--light': type.theme_name === 'light',
                        'con_stage__marker-content--dark': type.theme_name === 'dark',
                     }">{{ type.name }}</div>
                    <GlobalResponsiveImage class="con_stage__marker-image" :responsiveImage="type.image" :lazy="false" />
                </div>
            </template>
            <div class="con_stage__cursor">
                <div class="con_stage__click"></div>
                <div class="con_stage__pointer"></div>
            </div>
        </div>

        <template v-if="selectedType && isMobile">
            <template v-for="(bauteil, bauteilIndex) in selectedType.bauteile">
                <global-accordion accordion-type="3" :open="selectedBauteil === bauteil" @toggleAccordion="toggleSelectedBauteil(bauteil)" :use-content-stage="true">
                    <template slot="header"><span v-html="bauteil.name" /></template>
                    <constructionstage-bauteil slot="body" :bauteil="bauteil" :activeBreakpoint="activeBreakpoint" />
                </global-accordion>
            </template>
        </template>
        <template v-if="selectedType && !isMobile">
            <div class="con_stage__side">
                <div class="global-filter-list global-filter-list--vue">
                    <ul class="filter--list filter__body">
                        <li v-for="bauteil in selectedType.bauteile" :key="bauteil.name">
                            <button class="filter filter--secondary" :class="{ 'active': selectedBauteil === bauteil }" @click="setSelectedBauteil(bauteil)" v-html="bauteil.navigation_name"></button>
                        </li>
                    </ul>
                </div>

                <constructionstage-bauteil v-if="selectedBauteil" :bauteil="selectedBauteil" :activeBreakpoint="activeBreakpoint" />
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import GlobalResponsiveImage from "../../../../../global/global-elements/global-responsive-image/global-responsive-image.vue";
// @ts-ignore
import {detectBreakpoint} from "../../../../../utilities/sal-breakpoint/sal-breakpoint.js";
import ConstructionstageBauteil from "./constructionstage-bauteil.vue";
import GlobalAccordion from "../../../../../global/global-elements/global-accordion/global-accordion.vue";
// @ts-ignore
import {track_abstract, tracking_events} from '../../../../../utilities/track-abstract/track-abstract';
import {ResponsiveImage} from "../../../../../_types/image";

const SLIDER_SNAP_OFFSET = 10 // %
    let moveOutTimer: number;

    export interface ResponsivePosition {
        xl: ResponsivePositionValues
        lg: ResponsivePositionValues
        md: ResponsivePositionValues
        xs: ResponsivePositionValues
    }

    export interface ResponsivePositionValues {
        vertical: string
        horizontal: string
    }

    export interface Schicht {
        name: string
        position: ResponsivePosition
        url?: string
    }

    export interface Bauteil {
        name: string
        navigation_name: string
        description: string
        orientation: 'horizontal'|'vertical'
        label_top: string
        label_bottom: string
        image: ResponsiveImage
        schichten: Schicht[]
    }

    export interface Type {
        name: string
        image: ResponsiveImage
        theme_box: string
        theme_name: string
        position: ResponsivePosition
        bauteile?: Bauteil[]
    }

    export interface Configuration {
        types: Type[]
        image_fg?: ResponsiveImage
        image_bg: ResponsiveImage
        slider_to: number
        slider_initial: number
        slider_from: number
        slider_direction: 'rtl' | 'ltr'
    }

    export interface ConstructionStageData {
        animationRunning: boolean
        sliderInset: number
        sliderInsetAnimation: number|null
        sliderDirection: string
        showToHandle: boolean
        showFromHandle: boolean
        mouseDown: boolean
        mouseMoved: boolean
        hoverType: Type|null
        selectedType: Type|null
        selectedBauteil: Bauteil|null
        activeBreakpoint: string
        trackedSlideInteraction: boolean
        trackedTypeInteraction: boolean
    }

	export default Vue.extend({
        name: "constructionstage",
        components: {GlobalAccordion, ConstructionstageBauteil, GlobalResponsiveImage},
        props: {
            configuration: {
                type: Object as () => Configuration,
                required: true
            }
        },
        data(): ConstructionStageData {
            const initialBreakpoint = detectBreakpoint()
            const initialSliderInset = (initialBreakpoint === "xs" || initialBreakpoint === "sm")
                                        ? this.configuration.slider_from // Fixed to minimum value for mobile
                                        // And clamped between motion range for desktop
                                        : Math.min(Math.max(this.configuration.slider_initial, this.configuration.slider_from), this.configuration.slider_to)
            return {
                animationRunning: false,
                sliderInset: initialSliderInset,
                sliderInsetAnimation: null,
                sliderDirection: this.configuration.slider_direction,
                showToHandle: true, //initialSliderInset < this.configuration.slider_to,
                showFromHandle: true, //initialSliderInset > this.configuration.slider_from,
                mouseDown: false,
                mouseMoved: false,
                hoverType: null,
                selectedType: null,
                selectedBauteil: null,
                activeBreakpoint: initialBreakpoint,
                trackedSlideInteraction: false,
                trackedTypeInteraction: false
            }
        },
        computed: {
            isMobile() : boolean {
                return this.activeBreakpoint === "xs" || this.activeBreakpoint === "sm"
            },
            showSlider(): boolean {
                return !!this.configuration.image_fg;
            },
            sliderHandleStyle() {
                if (this.sliderDirection === 'rtl') {
                    return { right: this.sliderInset+'%' }
                }
                else {
                    return { left: this.sliderInset+'%' }
                }
            },
            sliderImageStyle() {
                if (this.sliderDirection === 'rtl') {
                    return { clipPath: `inset(0 ${this.sliderInset}% 0 0)` }
                }
                else {
                    return { clipPath: `inset(0 0 0 ${this.sliderInset}%)` }
                }
            },
            showRightHandle() : boolean {
                if (this.sliderDirection === 'rtl') {
                    return this.showFromHandle
                }
                else {
                    return this.showToHandle
                }
            },
            showLeftHandle() : boolean {
                if (this.sliderDirection === 'rtl') {
                    return this.showToHandle
                }
                else {
                    return this.showFromHandle
                }
            }
        },
        methods: {
            updateInset(value: number, animationEasing : 'instant'|'quick'|'ease-out' = 'ease-out', animationCallback: Function|null = null) {
                if (this.sliderInsetAnimation) {
                    cancelAnimationFrame(this.sliderInsetAnimation)
                }
                if (!this.$refs.mainstage) {
                    return
                }
                const mainstage = this.$refs.mainstage as HTMLElement

                const valuePerc = (value / mainstage.clientWidth) * 100
                let target = this.sliderInset
                if (valuePerc < this.configuration.slider_from) {
                    target = this.configuration.slider_from
                    this.showToHandle = true
                    this.showFromHandle = false
                } else if (valuePerc > this.configuration.slider_to) {
                    target = this.configuration.slider_to
                    this.showToHandle = false
                    this.showFromHandle = true
                } else {
                    target = valuePerc
                    this.showToHandle = true
                    this.showFromHandle = true
                }

                if (target != this.sliderInset) {
                    if (animationEasing === 'instant') {
                        this.sliderInset = target;
                    }
                    else {
                        const animate = () => {
                            if (animationEasing === 'ease-out') {
                                this.sliderInset += (target - this.sliderInset) * 0.1
                            } else if (animationEasing === 'quick') {
                                this.sliderInset += (target - this.sliderInset) * 0.2
                            }
                            if (Math.abs(this.sliderInset - target) > 0.1) {
                                this.sliderInsetAnimation = requestAnimationFrame(animate)
                            }
                            else if (animationCallback) {
                                this.sliderInset = target
                                animationCallback()
                            }
                        }
                        this.sliderInsetAnimation = requestAnimationFrame(animate);
                    }
                }
            },
            handleLeftHandlerClick() {
                if (this.animationRunning) {
                    return
                }
                if (!this.mouseMoved) {
                    this.trackSliderInteraction()
                    if (this.sliderDirection === 'rtl') {
                        this.updateInset(9999)
                    }
                    else {
                        this.updateInset(0)
                    }
                }
            },
            handleRightHandlerClick() {
                if (this.animationRunning) {
                    return
                }
                if (!this.mouseMoved) {
                    this.trackSliderInteraction()
                    if (this.sliderDirection === 'rtl') {
                        this.updateInset(0)
                    }
                    else {
                        this.updateInset(9999)
                    }
                }
            },
            handleMouseDown(event: MouseEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }
                // Ignore touchy touchy on con_stage__marker
                const targetElement = event.target as HTMLElement
                const markerElement = targetElement.closest(".con_stage__marker")
                if (markerElement) {
                    return
                }

                this.mouseDown = true
                this.mouseMoved = false
                const mainstage = this.$refs.mainstage as HTMLElement
                const rect = mainstage.getBoundingClientRect();
                this.updateInset(rect.width - (event.clientX - rect.left))
                event.preventDefault()
                this.trackSliderInteraction()
            },
            handleMouseUp(event?: MouseEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }

                // jump to the edge if it's released close to the edge
                if (this.mouseDown) {
                    const mainstage = this.$refs.mainstage as HTMLElement
                    if (this.sliderInset < SLIDER_SNAP_OFFSET) {
                        this.updateInset(0)
                    } else if (this.sliderInset > mainstage.clientWidth - SLIDER_SNAP_OFFSET) {
                        this.updateInset(mainstage.clientWidth)
                    }

                }
                // Always - this method can also be called by handleMouseLeave and we don't register the mouseup while we left the stage
                // So just make sure we stop pretending the mouse is down once we left for long enough ... ;-)
                this.mouseDown = false
                if (event) {
                    event.preventDefault()
                }
            },
            handleMouseMove(event: MouseEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }
                const mainstage = this.$refs.mainstage as HTMLElement
                if (this.mouseDown && event.target) {
                    if (moveOutTimer > 0) {
                        clearTimeout(moveOutTimer)
                        moveOutTimer = -1
                    }
                    this.mouseMoved = true
                    const rect = mainstage.getBoundingClientRect();
                    this.updateInset(rect.width - (event.clientX - rect.left), 'instant')
                    event.preventDefault()
                }
            },
            handleMouseLeave(event: MouseEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }
                if (this.mouseDown) {
                    moveOutTimer = setTimeout(this.handleMouseUp, 1000);
                }
            },
            handleTouchStart(event: TouchEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }
                if (event.touches && event.touches.length > 0) {
                    // Ignore touchy touchy on con_stage__marker
                    const touch = event.touches[0] as Touch
                    const targetElement = touch.target as HTMLElement
                    const markerElement = targetElement.closest(".con_stage__marker")
                    if (markerElement) {
                        return
                    }
                    const handleLeftElement = targetElement.closest(".con_stage__handle-left")
                    if (handleLeftElement) {
                        setTimeout(() => this.handleLeftHandlerClick(), 300)
                    }
                    const handleRightElement = targetElement.closest(".con_stage__handle-right")
                    if (handleRightElement) {
                        setTimeout(() => this.handleRightHandlerClick(), 300)
                    }

                    this.mouseDown = true
                    this.mouseMoved = false
                    const mainstage = this.$refs.mainstage as HTMLElement
                    this.updateInset(mainstage.clientWidth - (touch.clientX - mainstage.offsetLeft))
                    event.preventDefault()
                    this.trackSliderInteraction()
                }
            },
            handleTouchEnd(event: TouchEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }

                // jump to the edge if it's released close to the edge
                if (this.mouseDown) {
                    const mainstage = this.$refs.mainstage as HTMLElement
                    if (this.sliderInset < SLIDER_SNAP_OFFSET) {
                        this.updateInset(0)
                    } else if (this.sliderInset > mainstage.clientWidth - SLIDER_SNAP_OFFSET) {
                        this.updateInset(mainstage.clientWidth)
                    }

                    this.mouseDown = false
                    event.preventDefault()
                }
            },
            handleTouchMove(event: TouchEvent) {
                if (this.animationRunning) {
                    return
                }
                if (!this.$refs.mainstage) {
                    return
                }
                const mainstage = this.$refs.mainstage as HTMLElement
                if (this.mouseDown && event.touches && event.touches.length > 0) {
                    this.mouseMoved = true
                    const rect = mainstage.getBoundingClientRect();
                    this.updateInset(rect.width - (event.touches[0].clientX - rect.left), 'instant')
                    event.preventDefault()
                }
            },


            setHoverType(type: Type|null) {
                if (this.animationRunning) {
                    return
                }
                this.hoverType = type
                this.trackTypeInteraction()
            },
            toggleSelectedType(type: Type|null, event: MouseEvent) {
                if (this.animationRunning) {
                    return
                }
                if (type !== null && type.bauteile && type.bauteile.length > 0) {
                    this.selectedType = type
                    track_abstract.trigger_event(tracking_events.ConstructionStageSelectCategory, type.name);
                    if (!this.isMobile) {
                        this.setSelectedBauteil(type.bauteile[0])
                    }
                }
                event.preventDefault()
            },
            setSelectedBauteil(bauteil: Bauteil|null) {
                this.selectedBauteil = bauteil
                if (bauteil !== null) {
                    track_abstract.trigger_event(tracking_events.ConstructionStageSelectBauteil, bauteil.name);
                }
            },
            toggleSelectedBauteil(bauteil: Bauteil|null) {
                if (this.selectedBauteil === bauteil) {
                    this.setSelectedBauteil(null)
                }
                else {
                    this.setSelectedBauteil(bauteil)
                }
            },

            getStyleForMarker(position: ResponsivePosition) {
                const style = {} as {
                    top: string
                    right: string
                    bottom: string
                    left: string
                }

                let pos_h = position.xl.horizontal
                if ((this.activeBreakpoint === 'xs' || this.activeBreakpoint === 'sm') && position.xs.horizontal !== '%') {
                    pos_h = position.xs.horizontal
                }
                else if (this.activeBreakpoint === 'md' && position.md.horizontal !== '%') {
                    pos_h = position.md.horizontal
                }
                else if (this.activeBreakpoint === 'lg' && position.lg.horizontal !== '%') {
                    pos_h = position.lg.horizontal
                }

                let pos_v = position.xl.vertical
                if ((this.activeBreakpoint === 'xs' || this.activeBreakpoint === 'sm') && position.xs.vertical !== '%') {
                    pos_v = position.xs.vertical
                }
                else if (this.activeBreakpoint === 'md' && position.md.vertical !== '%') {
                    pos_v = position.md.vertical
                }
                else if (this.activeBreakpoint === 'lg' && position.lg.vertical !== '%') {
                    pos_v = position.lg.vertical
                }

                if (pos_h.startsWith('-')) {
                    style["right"] = pos_h.substr(1)
                }
                else {
                    style["left"] = pos_h
                }

                if (pos_v.startsWith('-')) {
                    style["bottom"] = pos_v.substr(1)
                }
                else {
                    style["top"] = pos_v
                }

                return style
            },

            handleResize() {
                this.activeBreakpoint = detectBreakpoint()
            },

            startAnimation() {
                if (window.TYPO3) {
                    return;
                }

                this.animationRunning = true

                const firstType = this.configuration.types[0]
                const secondType = this.configuration.types[1]

                const firstPosition = this.getStyleForMarker(firstType.position)
                const secondPosition = this.getStyleForMarker(secondType.position)

                this.$refs.mainstage.style.setProperty('--con-stage-anim-slide-from', this.sliderInset+'%')
                this.$refs.mainstage.style.setProperty('--con-stage-anim-slide-to', '50%')
                this.$refs.mainstage.style.setProperty('--con-stage-anim-first-top', firstPosition.top ? `calc(${firstPosition.top} + 25px)` : `calc(${(100 - parseInt(firstPosition.bottom)) + '%'} - 25px)`)
                this.$refs.mainstage.style.setProperty('--con-stage-anim-first-left', firstPosition.left ? `calc(${firstPosition.left} + 25px)` : `calc(${(100 - parseInt(firstPosition.right)) + '%'} - 25px)`)
                this.$refs.mainstage.style.setProperty('--con-stage-anim-second-top', secondPosition.top ? `calc(${secondPosition.top} + 25px)` : `calc(${(100 - parseInt(secondPosition.bottom)) + '%'} - 25px)`)
                this.$refs.mainstage.style.setProperty('--con-stage-anim-second-left', secondPosition.left ? `calc(${secondPosition.left} + 25px)` : `calc(${(100 - parseInt(secondPosition.right)) + '%'} - 25px)`)

                setTimeout(() => {
                    this.hoverType = firstType
                }, 1400)
                setTimeout(() => {
                    this.hoverType = null
                }, 2650)
                setTimeout(() => {
                    this.hoverType = secondType
                }, 3100)
                setTimeout(() => {
                    this.hoverType = null
                }, 5100)
            },

            stopAnimation() {
                this.animationRunning = false
                this.updateInset(this.$refs.mainstage.offsetWidth / 2, 'instant')
            },

            debugMousePosition(event: PointerEvent) {
                // @ts-ignore
                if (window.TYPO3) {
                    const clickedElement = event.target as HTMLElement
                    const pctTop = event.offsetY / clickedElement.clientHeight * 100
                    const pctLeft = event.offsetX / clickedElement.clientWidth * 100

                    console.log((this.activeBreakpoint === 'sm' ? 'xs' : this.activeBreakpoint) + ' :: horizontal from left edge %c'+(Math.round(pctLeft * 100) / 100).toFixed(2)+'%c % / from right edge %c'+(Math.round(pctLeft * 100) / 100 - 100).toFixed(2)+'%c %', 'font-weight: bold', 'font-weight: normal', 'font-weight: bold', 'font-weight: normal')
                    console.log((this.activeBreakpoint === 'sm' ? 'xs' : this.activeBreakpoint) + ' :: vertical from top edge %c'+(Math.round(pctTop * 100) / 100).toFixed(2)+'%c % / from bottom edge %c'+(Math.round(pctTop * 100) / 100 - 100).toFixed(2)+'%c %', 'font-weight: bold', 'font-weight: normal', 'font-weight: bold', 'font-weight: normal')
                }
            },

            trackSliderInteraction() {
                if (!this.trackedSlideInteraction) {
                    track_abstract.trigger_event(tracking_events.ConstructionStageSlideInteraction);
                    this.trackedSlideInteraction = true
                }
            },
            trackTypeInteraction() {
                if (!this.trackedTypeInteraction) {
                    track_abstract.trigger_event(tracking_events.ConstructionStageCategoryInteraction);
                    this.trackedTypeInteraction = true
                }
            }
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.handleResize);
        },
        mounted() {
            window.addEventListener('resize', this.handleResize);

            const observer = new IntersectionObserver((entries) => {
                // since there is a single target to be observed, there will be only one entry
                if(entries[0].intersectionRatio === 1) {
                    this.startAnimation()
                    observer.disconnect()
                }
            }, { threshold: [1] })
            observer.observe(this.$refs.mainstage);
        }
    })
</script>
